<template>
  <v-container fluid class="pa-0 fill-height">
    <!-- 🟢 Header -->
    <v-app-bar app color="primary" dark flat>
      <v-btn icon @click="$router.back()">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="font-weight-bold">
        Matchmaking Preferences
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn color="secondary" rounded small @click="savePreferences">
        <v-icon left>mdi-content-save</v-icon> Save
      </v-btn>
    </v-app-bar>

    <!-- 🟢 Progress Bar -->
    <!-- <v-container class="progress-bar-floating">
      <v-card>
        <v-card-text>
          <v-progress-linear
            :value="completionPercent"
            height="10"
            :color="progressColor"
            rounded
            striped
          />
          <div class="d-flex justify-space-between align-center mt-1 px-4 pb-2">
            <span class="text-caption grey--text text--darken-1">
              {{ progressMessage }}
            </span>
            <span
              class="font-weight-bold"
              :class="{
                'red--text': completionPercent < 30,
                'orange--text':
                  completionPercent >= 30 && completionPercent < 70,
                'green--text': completionPercent >= 70,
              }"
            >
              {{ completionPercent }}%
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-container> -->

    <v-container class="progress-bar-floating">
      <v-card class="glass-bar rounded-xl">
        <v-card-text>
          <v-progress-linear
            :value="matchPotential"
            height="10"
            :color="matchColor"
            rounded
            striped
          />
          <div class="d-flex justify-space-between align-center mt-1 px-4 pb-2">
            <span class="text-caption grey--text text--darken-1">
              {{ matchMessage }}
            </span>
            <span
              class="font-weight-bold"
              :class="{
                'green--text': matchPotential > 70,
                'orange--text': matchPotential <= 70 && matchPotential > 40,
                'red--text': matchPotential <= 40,
              }"
            >
              {{ matchPotential }}%
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- 🟢 Intro -->

    <v-container class="px-6" style="margin-top: 100px !important">
      <v-alert type="info" border="left" colored-border prominent>
        <div class="font-weight-medium">
          These preferences define who you are open to match with.
        </div>
        <div class="text-body-2 mt-1">
          As a <strong>Wali (Guardian)</strong> or individual, be honest and
          reasonable — the more flexible you are, the broader your matches will
          be. The more specific you are, the fewer but more tailored your
          results.
        </div>
      </v-alert>
    </v-container>

    <!-- 🟢 Form -->
    <v-container v-if="!loadingPrefs" class="pb-10 px-6">
      <!-- 👤 Gender -->
      <v-subheader class="text--primary font-weight-bold"
        >Looking For</v-subheader
      >
      <div class="d-flex justify-space-around mb-6 flex-wrap">
        <v-card
          v-for="g in allowedGenders"
          :key="g"
          class="pa-4 text-center gender-card mb-3"
          :elevation="prefs.lookingForGender === g ? 6 : 1"
          :color="prefs.lookingForGender === g ? 'accent lighten-5' : 'white'"
          @click="
            prefs.lookingForGender = g;
            $v.prefs.lookingForGender.$touch();
          "
        >
          <v-icon
            :color="prefs.lookingForGender === g ? 'primary' : 'grey darken-1'"
            size="36"
          >
            {{ g === "male" ? "mdi-human-male" : "mdi-human-female" }}
          </v-icon>
          <div class="mt-2 font-weight-medium text-capitalize">{{ g }}</div>
        </v-card>
      </div>
      <small v-if="lookingForErrors().length" class="error--text ml-1">
        {{ lookingForErrors()[0] }}
      </small>

      <!-- 🎂 Age Range -->
      <v-subheader class="text--primary font-weight-bold"
        >Age Range</v-subheader
      >
      <v-range-slider
        v-model="prefs.ageRange"
        :min="17"
        :max="65"
        thumb-label="always"
        color="primary"
      />
      <div class="text-caption text-center mb-6">
        {{ prefs.ageRange[0] }} — {{ prefs.ageRange[1] }} years
      </div>

      <!-- 🌍 Location -->
      <v-subheader class="text--primary font-weight-bold"
        >Location Preference</v-subheader
      >
      <v-radio-group v-model="prefs.locationType" row>
        <v-radio label="Any Location" value="any" />
        <v-radio label="Specific" value="specific" />
      </v-radio-group>

      <v-slide-y-transition>
        <div v-if="prefs.locationType === 'specific'">
          <v-select
            v-model="prefs.countries"
            :items="countries"
            label="Preferred Country(s)"
            multiple
            chips
            outlined
            :menu-props="{ offsetY: true }"
            dense
            color="primary"
          />
          <!-- 🌍 State Selection Grouped by Country -->
          <div v-if="prefs.countries && prefs.countries.length">
            <v-subheader class="text--primary font-weight-bold">
              Preferred State(s)
            </v-subheader>

            <v-menu offset-y max-height="400">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  v-on="on"
                  label="Select State(s)"
                  outlined
                  dense
                  readonly
                  color="primary"
                  :value="
                    prefs.states && prefs.states.length
                      ? `${prefs.states.length} selected`
                      : 'None selected'
                  "
                  append-icon="mdi-menu-down"
                />
              </template>

              <v-list dense class="py-0">
                <!-- Loop through selected countries -->
                <template v-for="country in prefs.countries">
                  <v-subheader
                    :key="country"
                    class="font-weight-bold text--primary mt-2"
                  >
                    {{ country }}
                  </v-subheader>

                  <!-- Select All for each country -->
                  <v-list-item @click="toggleCountryStates(country)" ripple>
                    <v-list-item-action>
                      <v-checkbox
                        :input-value="isCountryFullySelected(country)"
                        color="primary"
                        hide-details
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title
                        >Select All {{ country }}</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>

                  <!-- Individual States -->
                  <v-list-item
                    v-for="state in statesByCountry[country]"
                    :key="`${country}-${state}`"
                    ripple
                    @click="toggleState(country, state)"
                  >
                    <v-list-item-action>
                      <v-checkbox
                        :input-value="prefs.states.includes(state)"
                        color="primary"
                        hide-details
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ state }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider class="my-1" />
                </template>
              </v-list>
            </v-menu>

            <!-- Show selected as chips -->
            <div class="mt-3">
              <v-chip
                v-for="state in prefs.states"
                :key="state"
                small
                outlined
                color="primary"
                class="ma-1"
                close
                @click:close="removeState(state)"
              >
                {{ state }}
              </v-chip>
            </div>
          </div>
        </div>
      </v-slide-y-transition>

      <!-- 🕌 Madhab -->
      <v-subheader class="text--primary font-weight-bold mt-6"
        >Madhab</v-subheader
      >
      <v-radio-group
        v-model="prefs.madhabType"
        :error-messages="madhabErrors()"
        @change="$v.prefs.madhabType.$touch()"
        row
      >
        <v-radio label="Any" value="any" />
        <v-radio label="Specific" value="specific" />
      </v-radio-group>

      <v-slide-y-transition>
        <div v-if="prefs.madhabType === 'specific'">
          <v-select
            v-model="prefs.madhabs"
            :items="madhabOptions"
            label="Select Madhab(s)"
            outlined
            dense
            :menu-props="{ offsetY: true }"
            color="primary"
            multiple
            chips
          />
        </div>
      </v-slide-y-transition>

      <!-- 💍 Marital Status -->
      <v-subheader class="text--primary font-weight-bold mt-6"
        >Marital Status</v-subheader
      >

      <v-radio-group
        v-model="prefs.maritalStatusType"
        :error-messages="maritalStatusErrors()"
        @change="$v.prefs.maritalStatusType.$touch()"
        row
      >
        <v-radio label="Any" value="any" />
        <v-radio label="Specific" value="specific" />
      </v-radio-group>

      <v-slide-y-transition>
        <div v-if="prefs.maritalStatusType === 'specific'">
          <v-select
            v-model="prefs.maritalStatuses"
            :items="maritalStatusOptions"
            multiple
            chips
            :menu-props="{ offsetY: true }"
            label="Select acceptable statuses"
            outlined
            dense
            color="primary"
          >
            <template v-slot:prepend-item>
              <v-list-item
                ripple
                @click="
                  toggleSelectAll('maritalStatuses', maritalStatusOptions)
                "
              >
                <v-list-item-action>
                  <v-checkbox
                    :input-value="
                      isAllSelected('maritalStatuses', maritalStatusOptions)
                    "
                    color="primary"
                    hide-details
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
          </v-select>
        </div>
      </v-slide-y-transition>

      <v-subheader class="text--primary font-weight-bold mt-6"
        >Ethnicity</v-subheader
      >

      <v-radio-group
        v-model="prefs.ethnicityType"
        :error-messages="ethnicityErrors()"
        @change="$v.prefs.ethnicityType.$touch()"
        row
      >
        <v-radio label="Any" value="any" />
        <v-radio label="Specific" value="specific" />
      </v-radio-group>

      <v-slide-y-transition>
        <div v-if="prefs.ethnicityType === 'specific'">
          <v-select
            v-model="prefs.ethnicities"
            :items="ethnicityOptions"
            multiple
            chips
            :menu-props="{ offsetY: true }"
            label="Select one or more ethnicities"
            outlined
            dense
            color="primary"
          >
            <template v-slot:prepend-item>
              <v-list-item
                ripple
                @click="toggleSelectAll('ethnicities', ethnicityOptions)"
              >
                <v-list-item-action>
                  <v-checkbox
                    :input-value="
                      isAllSelected('ethnicities', ethnicityOptions)
                    "
                    color="primary"
                    hide-details
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
          </v-select>
        </div>
      </v-slide-y-transition>

      <v-subheader class="text--primary font-weight-bold mt-6"
        >Legal Status</v-subheader
      >

      <v-radio-group
        v-model="prefs.legalStatusType"
        :error-messages="legalStatusErrors()"
        @change="$v.prefs.legalStatusType.$touch()"
        row
      >
        <v-radio label="Any" value="any" />
        <v-radio label="Specific" value="specific" />
      </v-radio-group>

      <v-slide-y-transition>
        <div v-if="prefs.legalStatusType === 'specific'">
          <v-select
            v-model="prefs.legalStatuses"
            :items="legalStatusOptions"
            multiple
            chips
            :menu-props="{ offsetY: true }"
            label="Acceptable Legal Statuses"
            outlined
            dense
            color="primary"
          >
            <template v-slot:prepend-item>
              <v-list-item
                ripple
                @click="toggleSelectAll('legalStatuses', legalStatusOptions)"
              >
                <v-list-item-action>
                  <v-checkbox
                    :input-value="
                      isAllSelected('legalStatuses', legalStatusOptions)
                    "
                    color="primary"
                    hide-details
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
          </v-select>
        </div>
      </v-slide-y-transition>

      <v-subheader class="text--primary font-weight-bold mt-6"
        >Education Level</v-subheader
      >

      <v-radio-group
        v-model="prefs.educationType"
        :error-messages="educationErrors()"
        @change="$v.prefs.educationType.$touch()"
        row
      >
        <v-radio label="Any" value="any" />
        <v-radio label="Specific" value="specific" />
      </v-radio-group>

      <v-slide-y-transition>
        <div v-if="prefs.educationType === 'specific'">
          <v-select
            v-model="prefs.educationLevels"
            :items="educationOptions"
            multiple
            chips
            :menu-props="{ offsetY: true }"
            label="Preferred Education Levels"
            outlined
            dense
            color="primary"
          >
            <template v-slot:prepend-item>
              <v-list-item
                ripple
                @click="toggleSelectAll('educationLevels', educationOptions)"
              >
                <v-list-item-action>
                  <v-checkbox
                    :input-value="
                      isAllSelected('educationLevels', educationOptions)
                    "
                    color="primary"
                    hide-details
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
          </v-select>
        </div>
      </v-slide-y-transition>

      <v-subheader class="text--primary font-weight-bold mt-6"
        >Islamic Education</v-subheader
      >

      <v-radio-group
        v-model="prefs.islamicEducationType"
        :error-messages="islamicEducationErrors()"
        @change="$v.prefs.islamicEducationType.$touch()"
        row
      >
        <v-radio label="Any" value="any" />
        <v-radio label="Specific" value="specific" />
      </v-radio-group>

      <v-slide-y-transition>
        <div v-if="prefs.islamicEducationType === 'specific'">
          <v-select
            v-model="prefs.islamicEducationLevels"
            :items="islamicEduOptions"
            multiple
            chips
            :menu-props="{ offsetY: true }"
            label="Preferred Islamic Education Levels"
            outlined
            dense
            color="primary"
          >
            <template v-slot:prepend-item>
              <v-list-item
                ripple
                @click="
                  toggleSelectAll('islamicEducationLevels', islamicEduOptions)
                "
              >
                <v-list-item-action>
                  <v-checkbox
                    :input-value="
                      isAllSelected('islamicEducationLevels', islamicEduOptions)
                    "
                    color="primary"
                    hide-details
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
          </v-select>
        </div>
      </v-slide-y-transition>

      <!-- 👶 Children -->
      <v-subheader class="text--primary font-weight-bold mt-6"
        >Children</v-subheader
      >
      <div class="text-body-2 mb-2">
        Are you open to marrying someone with children?
      </div>
      <v-radio-group v-model="prefs.considerWithChildren" row>
        <v-radio label="Yes" value="true" />
        <v-radio label="No" value="false" />
      </v-radio-group>

      <div class="text-body-2 mb-2">
        Does your partner need to want children?
      </div>
      <v-radio-group v-model="prefs.partnerWantsChildren" row>
        <v-radio label="Yes" value="true" />
        <v-radio label="No" value="false" />
      </v-radio-group>

      <!-- ☪️ Accept Revert -->
      <v-subheader class="text--primary font-weight-bold mt-6">
        Revert (Convert to Islam)
      </v-subheader>
      <div class="text-caption grey--text text--darken-1 mb-1 ml-1">
        Are you open to marrying a revert (convert to Islam)?
      </div>

      <v-radio-group
        v-model="prefs.considerRevert"
        :mandatory="false"
        row
        @change="updateProgress"
      >
        <v-radio label="Yes" value="yes" />
        <v-radio label="No" value="no" />
        <v-radio label="Doesn’t matter" value="any" />
      </v-radio-group>

      <!-- ♿ Disabilities -->
      <v-subheader class="text--primary font-weight-bold mt-6">
        Disabilities
      </v-subheader>
      <div class="text-caption grey--text text--darken-1 mb-1 ml-1">
        Are you open to someone with disabilities?
      </div>

      <v-radio-group
        v-model="prefs.considerWithDisabilities"
        :mandatory="false"
        row
        @change="updateProgress"
      >
        <v-radio label="Yes" value="yes" />
        <v-radio label="No" value="no" />
        <v-radio label="Doesn’t matter" value="any" />
      </v-radio-group>

      <!-- 🚬 Habits -->
      <v-subheader class="text--primary font-weight-bold mt-6"
        >Lifestyle Habits</v-subheader
      >
      <v-radio-group v-model="prefs.smokesPreference" row>
        <v-radio label="Doesn't matter" value="any" />
        <v-radio label="Non-smoker only" value="no" />
        <v-radio label="Smoker acceptable" value="yes" />
      </v-radio-group>

      <v-radio-group v-model="prefs.drinksPreference" row>
        <v-radio label="Doesn't matter" value="any" />
        <v-radio label="Non-drinker only" value="no" />
        <v-radio label="Drinker acceptable" value="yes" />
      </v-radio-group>

      <!-- 🌍 Relocation -->
      <v-subheader class="text--primary font-weight-bold mt-6">
        Relocation
      </v-subheader>
      <div class="text-caption grey--text text--darken-1 mb-1 ml-1">
        Would you be willing to relocate for marriage?
      </div>

      <v-radio-group
        v-model="prefs.willingToRelocate"
        :mandatory="false"
        row
        @change="updateProgress"
      >
        <v-radio label="Yes" value="yes" />
        <v-radio label="No" value="no" />
        <v-radio label="Doesn’t matter" value="any" />
      </v-radio-group>

      <!-- Save -->
      <v-btn
        block
        color="primary"
        rounded
        large
        class="mt-8 mb-10"
        @click="savePreferences"
      >
        <v-icon left>mdi-content-save</v-icon> Save Preferences
      </v-btn>
    </v-container>
    <v-container v-else class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="40" />
    </v-container>
  </v-container>
</template>

<script>
import profileOptions from "@/modules/Profile/config/profileOptions";
import { mapGetters, mapActions } from "vuex/dist/vuex.common.js";
import userService from "@/modules/User/service.js";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";

const notAny = (value) => value && value !== "any";

export default {
  data() {
    return {
      loadingPrefs: false,
      ...profileOptions,
      prefs: {
        lookingForGender: "any",
        ageRange: [18, 65],
        locationType: "any",
        country: null,
        state: null,
        madhabType: "any",
        madhabs: [],
        maritalStatuses: [],
        ethnicities: [],
        legalStatuses: [],
        educationLevels: [],
        islamicEducationLevels: [],
        considerWithChildren: null,
        partnerWantsChildren: null,
        considerRevert: false,
        considerWithDisabilities: false,
        smokesPreference: "any",
        drinksPreference: "any",
        willingToRelocate: false,
        countries: [], // ✅ default empty array
        states: [], // ✅ default empty array
      },
      completionPercent: 0,
      matchPotential: 100,
      matchColor: "green",
    };
  },
  computed: {
    ...mapGetters("global", [
      "currentUser",
      "currentOrganization",
      "globalStatus",
      "loadingStatus",
      "loginDialog",
    ]),
    allowedGenders() {
      if (this.currentUser?.gender === "Male") return ["female"];
      if (this.currentUser?.gender === "Female") return ["male"];
      return ["male", "female"]; // remove 'any'
    },
    matchMessage() {
      if (this.matchPotential > 80)
        return "💚 Excellent! You’re open to many great matches.";
      if (this.matchPotential > 60)
        return "🧡 Good balance — your filters are reasonable.";
      if (this.matchPotential > 40)
        return "💛 You’re being selective — expect fewer matches.";
      if (this.matchPotential > 20)
        return "❤️ Quite specific — only a few matches will appear.";
      return "❗Very limited — try relaxing some filters for better results.";
    },
    availableStates() {
      if (!this.prefs.countries || !this.prefs.countries.length) return [];
      const merged = [];
      this.prefs.countries.forEach((c) => {
        const list = this.statesByCountry[c] || [];
        merged.push(...list);
      });
      // remove duplicates and sort alphabetically
      return [...new Set(merged)].sort();
    },
    progressColor() {
      if (this.completionPercent < 30) return "error";
      if (this.completionPercent < 70) return "warning";
      return "success";
    },
    progressMessage() {
      if (this.completionPercent < 25)
        return "Add more info for better matches!";
      if (this.completionPercent < 50) return "You're getting started nicely!";
      if (this.completionPercent < 75)
        return "Almost complete! Refine your matches.";
      return "Perfect! Your preferences are well defined.";
    },
  },
  watch: {
    prefs: {
      deep: true,
      handler() {
        this.updateProgress();
      },
    },
  },
  async created() {
    if (!this.currentUser?.id) return;
    await this.loadPreferences();
  },
  mounted() {
    // Example: user is male → looking for female
    if (this.currentUser?.gender === "Male") {
      this.prefs.lookingForGender = "female";
    } else if (this.currentUser?.gender === "Female") {
      this.prefs.lookingForGender = "male";
    }
    this.updateProgress();
  },
  mixins: [validationMixin],
  validations: {
    prefs: {
      lookingForGender: { required, notAny },
      madhabType: { required },
      maritalStatusType: { required },
      ethnicityType: { required },
      legalStatusType: { required },
      educationType: { required },
      islamicEducationType: { required },
    },
  },
  methods: {
    ...mapActions("global", ["initUser"]),
    async loadPreferences() {
      try {
        const res = await userService.getSearchPreferences(this.currentUser.id);
        if (res?.data) {
          console.log("🔄 Loading existing preferences from backend.", res);
          const prefs = res.data;
          // Map backend fields back into your component’s model
          this.prefs = {
            ...this.prefs, // keep existing defaults
            lookingForGender:
              prefs.lookingForGender || this.prefs.lookingForGender,
            ageRange: [prefs.ageMin || 18, prefs.ageMax || 65],
            locationType: prefs.locationType || "any",
            countries: prefs.countries || [],
            states: prefs.states || [],
            madhabType: prefs.madhabType || "any",
            madhabs: prefs.madhabs || [],
            maritalStatusType: prefs.maritalStatusType || "any",
            maritalStatuses: prefs.maritalStatuses || [],
            ethnicityType: prefs.ethnicityType || "any",
            ethnicities: prefs.ethnicities || [],
            legalStatusType: prefs.legalStatusType || "any",
            legalStatuses: prefs.legalStatuses || [],
            educationType: prefs.educationType || "any",
            educationLevels: prefs.educationLevels || [],
            islamicEducationType: prefs.islamicEducationType || "any",
            islamicEducationLevels: prefs.islamicEducationLevels || [],
            considerWithChildren: prefs.considerWithChildren || null,
            partnerWantsChildren: prefs.partnerWantsChildren || null,
            considerRevert: prefs.considerRevert || "any",
            considerWithDisabilities: prefs.considerWithDisabilities || "any",
            smokesPreference: prefs.smokesPreference || "any",
            drinksPreference: prefs.drinksPreference || "any",
            willingToRelocate: prefs.willingToRelocate || "any",
          };
          console.log("✅ Loaded user preferences:", this.prefs);
        } else {
          console.log("ℹ️ No existing preferences found — using defaults.");
        }
      } catch (error) {
        console.error("⚠️ Failed to load preferences:", error);
      }
    },
    lookingForErrors() {
      const errors = [];
      if (!this.$v.prefs.lookingForGender.$dirty) return errors;

      if (!this.$v.prefs.lookingForGender.required)
        errors.push("Please select who you’re looking for.");
      else if (!this.$v.prefs.lookingForGender.notAny)
        errors.push("You must select Male or Female (not ‘Any’).");

      return errors;
    },
    madhabErrors() {
      const errors = [];
      if (!this.$v.prefs.madhabType.$dirty) return errors;
      if (!this.$v.prefs.madhabType.required)
        errors.push("Please choose ‘Any’ or ‘Specific’ for Madhab.");
      return errors;
    },
    maritalStatusErrors() {
      const errors = [];
      if (!this.$v.prefs.maritalStatusType.$dirty) return errors;
      if (!this.$v.prefs.maritalStatusType.required)
        errors.push("Please choose ‘Any’ or ‘Specific’ for Marital Status.");
      return errors;
    },
    ethnicityErrors() {
      const errors = [];
      if (!this.$v.prefs.ethnicityType.$dirty) return errors;
      if (!this.$v.prefs.ethnicityType.required)
        errors.push("Please choose ‘Any’ or ‘Specific’ for Ethnicity.");
      return errors;
    },
    legalStatusErrors() {
      const errors = [];
      if (!this.$v.prefs.legalStatusType.$dirty) return errors;
      if (!this.$v.prefs.legalStatusType.required)
        errors.push("Please choose ‘Any’ or ‘Specific’ for Legal Status.");
      return errors;
    },
    educationErrors() {
      const errors = [];
      if (!this.$v.prefs.educationType.$dirty) return errors;
      if (!this.$v.prefs.educationType.required)
        errors.push("Please choose ‘Any’ or ‘Specific’ for Education Level.");
      return errors;
    },
    islamicEducationErrors() {
      const errors = [];
      if (!this.$v.prefs.islamicEducationType.$dirty) return errors;
      if (!this.$v.prefs.islamicEducationType.required)
        errors.push("Please choose ‘Any’ or ‘Specific’ for Islamic Education.");
      return errors;
    },
    updateProgress() {
      const totalFields = Object.keys(this.prefs).length;
      let opennessScore = 0;

      Object.entries(this.prefs).forEach(([key, value]) => {
        // 🚫 Skip fields that should not affect match openness
        if (["lookingForGender"].includes(key)) return;

        if (Array.isArray(value)) {
          if (value.length === 0) opennessScore += 1; // completely open
          else opennessScore += 0.5; // partially open
        } else if (typeof value === "string") {
          if (value === "any" || value.trim() === "") opennessScore += 1;
          else opennessScore += 0.2;
        } else if (typeof value === "boolean") {
          opennessScore += 0.5;
        }
      });

      this.matchPotential = Math.min(
        Math.round((opennessScore / totalFields) * 100),
        100
      );

      if (this.matchPotential > 70) this.matchColor = "green";
      else if (this.matchPotential > 40) this.matchColor = "orange";
      else this.matchColor = "red";
    },
    toggleState(country, state) {
      const idx = this.prefs.states.indexOf(state);
      if (idx >= 0) {
        this.prefs.states.splice(idx, 1);
      } else {
        this.prefs.states.push(state);
      }
    },

    removeState(state) {
      this.prefs.states = this.prefs.states.filter((s) => s !== state);
    },

    toggleCountryStates(country) {
      const countryStates = this.statesByCountry[country] || [];
      const allSelected = countryStates.every((s) =>
        this.prefs.states.includes(s)
      );

      if (allSelected) {
        // Deselect all for that country
        this.prefs.states = this.prefs.states.filter(
          (s) => !countryStates.includes(s)
        );
      } else {
        // Select all for that country
        const newStates = [
          ...new Set([...this.prefs.states, ...countryStates]),
        ];
        this.prefs.states = newStates;
      }
    },

    isCountryFullySelected(country) {
      const countryStates = this.statesByCountry[country] || [];
      return (
        countryStates.length > 0 &&
        countryStates.every((s) => this.prefs.states.includes(s))
      );
    },
    isAllSelected(key, items) {
      const selected = this.prefs[key] || [];
      return selected.length === items.length;
    },
    toggleSelectAll(key, items) {
      const selected = this.prefs[key] || [];
      if (selected.length === items.length) {
        this.prefs[key] = []; // unselect all
      } else {
        this.prefs[key] = [...items]; // select all
      }
    },
    async savePreferences() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal({
          icon: "error",
          title: "Incomplete Preferences",
          text: "Please complete all required sections before saving.",
          confirmButtonColor: "#E53935",
        });
        return;
      }

      try {
        const payload = {
          UserId: this.currentUser.id,
          lookingForGender: this.prefs.lookingForGender,
          ageMin: this.prefs.ageRange[0],
          ageMax: this.prefs.ageRange[1],
          locationType: this.prefs.locationType,
          countries: this.prefs.countries,
          states: this.prefs.states,
          madhabType: this.prefs.madhabType,
          madhabs: this.prefs.madhabs,
          maritalStatusType: this.prefs.maritalStatusType,
          maritalStatuses: this.prefs.maritalStatuses,
          ethnicityType: this.prefs.ethnicityType,
          ethnicities: this.prefs.ethnicities,
          legalStatusType: this.prefs.legalStatusType,
          legalStatuses: this.prefs.legalStatuses,
          educationType: this.prefs.educationType,
          educationLevels: this.prefs.educationLevels,
          islamicEducationType: this.prefs.islamicEducationType,
          islamicEducationLevels: this.prefs.islamicEducationLevels,
        };

        await userService.saveSearchPreferences(payload);

        await userService.update(this.currentUser.id, {
          isPreferenceCompleted: true,
        });

        await this.initUser();

        this.$swal({
          icon: "success",
          title: "Preferences Saved!",
          html: `
        <p>Your matchmaking preferences have been updated successfully.</p>
        <p>You can now explore suitable profiles Insha'Allah 💚</p>
      `,
          confirmButtonText: "Go to Home",
          confirmButtonColor: "#2A6465",
        });

        this.$router.push("/");
      } catch (error) {
        console.error("❌ savePreferences error:", error);
        this.$swal({
          icon: "error",
          title: "Save Failed",
          text: "Something went wrong while saving your preferences. Please try again later.",
          confirmButtonColor: "#E53935",
        });
      }
    },
  },
};
</script>

<style scoped>
.progress-bar-floating {
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 8px 16px;
}
.gender-card {
  border-radius: 16px;
  width: 44%;
  transition: all 0.25s ease;
  cursor: pointer;
}
.gender-card:hover {
  transform: translateY(-2px);
}
.v-list-item.select-all {
  background-color: #f8f9fa !important;
  font-weight: 500;
}
.glass-bar {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}
</style>